import { useState, type FormEvent } from 'react';
import { useCreateMtoRunMutation } from '@shared/api';
import { apiErrorMessage } from '@entities/mto';
import { ACCEPT_ANY } from '@shared/lib/fileAccept';
import { Button } from '@shared/ui/Button';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { IconFile, IconTrash } from '@shared/ui/icons';
import {
  Err,
  FileItem,
  FileList,
  FileName,
  Footer,
  FooterLeft,
  Hint,
  Muted,
  RemoveBtn,
} from '../styles';

export function FilesMode({
  canWrite,
  onRunCreated,
}: {
  canWrite: boolean;
  onRunCreated: (runId: string) => void;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [createRun, { isLoading: creatingFiles }] = useCreateMtoRunMutation();

  async function submitFiles(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (files.length === 0) {
      setError('Приложите документы РД — PDF или таблицы Excel.');
      return;
    }
    const fd = new FormData();
    fd.append('module', 'rd_extract');
    for (const f of files) fd.append('rd', f);
    try {
      const res = await createRun(fd).unwrap();
      setFiles([]);
      onRunCreated(res.run_id);
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Не удалось запустить обработку. Попробуйте ещё раз.');
    }
  }

  return (
    <form onSubmit={submitFiles}>
      <Hint>
        PDF комплектов РД или таблицы Excel со спецификацией. Если документ уже есть в библиотеке
        РД — возьмём готовый разбор ПТО; иначе разберём таблицы спецификаций прямо здесь (сканы
        распознаются, это дольше). Библиотеку ПТО это не меняет.
      </Hint>
      {canWrite ? (
        <FileDropZone
          multiple
          compact
          accept={ACCEPT_ANY}
          hint="Или нажмите, чтобы выбрать. PDF, Excel, скан"
          onFiles={(added) => {
            setError(null);
            setFiles((prev) => [...prev, ...added]);
          }}
        />
      ) : (
        <Muted>Только просмотр — загрузка недоступна.</Muted>
      )}
      {files.length > 0 && (
        <FileList>
          {files.map((f, i) => (
            <FileItem key={`${f.name}-${i}`}>
              <IconFile size={15} />
              <FileName title={f.name}>{f.name}</FileName>
              <RemoveBtn
                type="button"
                aria-label={`Убрать файл ${f.name}`}
                onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
              >
                <IconTrash size={16} />
              </RemoveBtn>
            </FileItem>
          ))}
        </FileList>
      )}
      {error && <Err role="alert">{error}</Err>}
      <Footer>
        <FooterLeft>Файлов: {files.length}</FooterLeft>
        <Button
          type="submit"
          $variant="primary"
          disabled={!canWrite || creatingFiles || files.length === 0}
        >
          {creatingFiles ? 'Запуск…' : 'Сформировать ведомость'}
        </Button>
      </Footer>
    </form>
  );
}
